import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";
import type { TagSummary } from "@media-viewer/shared";
import { BatchTagPicker } from "../components/BatchTagPicker";

// Cap simultaneous uploads so the browser's connection pool and the
// server aren't overwhelmed when the user drops a folder of hundreds
// of files. Per-file chunks are sequential server-side anyway; the
// parallelism is across files, not within them.
const UPLOAD_CONCURRENCY = 5;

interface QueueItem {
  id: string;
  file: File;
  status: "pending" | "hashing" | "uploading" | "ok" | "duplicate" | "error";
  progress: number;
  message?: string;
  resultId?: number;
}

export function Upload() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const qc = useQueryClient();

  const [sessionTags, setSessionTags] = useState<TagSummary[]>([]);
  // Mirror into a ref so in-flight uploads read the latest tags at finalize
  // time, not the value captured when the upload started.
  const sessionTagsRef = useRef<TagSummary[]>([]);
  function updateSessionTags(next: TagSummary[]) {
    sessionTagsRef.current = next;
    setSessionTags(next);
  }

  // Pending queue + worker count. Workers run async, so we use a ref —
  // setState would not synchronize across the workers' picks. The
  // pendingRef.current array shrinks as workers pull items; activeRef
  // tracks how many are in flight so we know when to spawn more.
  const pendingRef = useRef<QueueItem[]>([]);
  const activeRef = useRef(0);

  function addFiles(files: FileList | File[]) {
    const next: QueueItem[] = Array.from(files).map((f) => ({
      id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2)}`,
      file: f,
      status: "pending",
      progress: 0,
    }));
    setItems((prev) => [...prev, ...next]);
    pendingRef.current.push(...next);
    pumpQueue();
  }

  // Spawn workers until UPLOAD_CONCURRENCY are running or the queue is
  // empty. Each worker, when its upload finishes, recurses to pump again
  // — this also handles the case where the user drops more files mid-run.
  function pumpQueue(): void {
    while (activeRef.current < UPLOAD_CONCURRENCY && pendingRef.current.length > 0) {
      const item = pendingRef.current.shift()!;
      activeRef.current++;
      void uploadOne(item).finally(() => {
        activeRef.current--;
        pumpQueue();
      });
    }
  }

  async function uploadOne(item: QueueItem) {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "hashing" } : i)));
    try {
      const result = await api.upload(
        item.file,
        (pct, phase) => {
          setItems((prev) =>
            prev.map((i) =>
              i.id === item.id
                ? { ...i, progress: pct, status: phase === "hashing" ? "hashing" : "uploading" }
                : i,
            ),
          );
        },
        { getTagIds: () => sessionTagsRef.current.map((t) => t.id) },
      );
      if ("duplicate" in result && result.duplicate) {
        const existingId = result.existingId;
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id
              ? { ...i, status: "duplicate", progress: 100, resultId: existingId, message: `already exists as #${existingId}` }
              : i,
          ),
        );
      } else {
        const ok = result;
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id
              ? { ...i, status: "ok", progress: 100, resultId: ok.id, message: ok.relativePath }
              : i,
          ),
        );
      }
      // Refresh listings
      qc.invalidateQueries({ queryKey: ["media"] });
    } catch (err) {
      const msg = err instanceof ApiError ? `HTTP ${err.status}` : String(err);
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "error", message: msg } : i)),
      );
    }
  }

  return (
    <div className="p-6">
      <h2 className="mb-4 text-2xl font-semibold">Upload media</h2>
      <div className="mb-4">
        <BatchTagPicker selected={sessionTags} onChange={updateSessionTags} />
      </div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
          }
        }}
        className={
          "rounded border-2 border-dashed p-12 text-center transition-colors " +
          (dragOver ? "border-sky-500 bg-sky-950/30" : "border-zinc-700 bg-zinc-900/50")
        }
      >
        <p className="mb-4 text-zinc-300">Drop files here, or pick by type</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {/*
           * Two buttons with type-specific accept= filters. This routes
           * the OS picker to the right place:
           *   - iOS: opens the Photos picker (proper Select / Select All).
           *   - Android (Samsung One UI in particular): opens Gallery,
           *     where tapping the round selector circles or long-pressing
           *     enters multi-select mode and "Select All" works.
           * A combined image/*,video/* accept on Samsung sometimes falls
           * back to "My Files" where Select All is grayed out, so we
           * split it.
           */}
          <label className="inline-block cursor-pointer rounded bg-emerald-700/40 px-4 py-2 text-emerald-100 hover:bg-emerald-700/60">
            <input
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            🖼 Photos
          </label>
          <label className="inline-block cursor-pointer rounded bg-sky-700/40 px-4 py-2 text-sky-100 hover:bg-sky-700/60">
            <input
              type="file"
              multiple
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            🎬 Videos
          </label>
          <label className="inline-block cursor-pointer rounded bg-zinc-800 px-4 py-2 hover:bg-zinc-700">
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            Other / files…
          </label>
        </div>
        <p className="mt-3 text-xs text-zinc-500 sm:hidden">
          Tip: in the Samsung Gallery picker, tap the round circle on a
          thumbnail (or long-press) to enter multi-select; "Select All"
          activates after the first item is chosen.
        </p>
        <p className="mt-2 text-xs text-zinc-500">
          Photos and videos auto-arranged into <code>photos/YYYY/MM/</code> or <code>videos/YYYY/MM/</code>.
        </p>
      </div>

      {items.length > 0 && (() => {
        const done = items.filter((i) => i.status === "ok" || i.status === "duplicate").length;
        const uploading = items.filter((i) => i.status === "uploading" || i.status === "hashing").length;
        const pending = items.filter((i) => i.status === "pending").length;
        const errors = items.filter((i) => i.status === "error").length;
        return (
          <div className="mt-6 mb-2 flex flex-wrap items-center gap-4 text-xs text-zinc-400">
            <span><span className="text-zinc-200">{done}</span> / {items.length} uploaded</span>
            {uploading > 0 && <span>· <span className="text-sky-300">{uploading}</span> in flight</span>}
            {pending > 0 && <span>· <span className="text-zinc-300">{pending}</span> queued</span>}
            {errors > 0 && <span>· <span className="text-red-400">{errors}</span> failed</span>}
          </div>
        );
      })()}
      {items.length > 0 && (
        <ul className="divide-y divide-zinc-800 rounded border border-zinc-800 bg-zinc-900">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-3 p-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-zinc-200">{it.file.name}</div>
                {it.message && <div className="truncate text-xs text-zinc-500">{it.message}</div>}
              </div>
              <div className="w-40">
                {(it.status === "hashing" || it.status === "uploading" || it.status === "pending") && (
                  <>
                    {it.status === "hashing" && (
                      <div className="mb-0.5 text-[10px] text-zinc-500">checking… {it.progress}%</div>
                    )}
                    <div className="h-1 overflow-hidden rounded bg-zinc-800">
                      <div
                        className="h-full bg-zinc-400 transition-all"
                        style={{ width: `${it.progress}%` }}
                      />
                    </div>
                  </>
                )}
                {it.status === "ok" && <span className="text-xs text-emerald-400">uploaded</span>}
                {it.status === "duplicate" && (
                  <a href={`/media/${it.resultId}`} className="text-xs text-amber-400 underline">
                    duplicate (view existing)
                  </a>
                )}
                {it.status === "error" && <span className="text-xs text-red-400">error</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
